import { Dialog } from "@/components/ui/Dialog"
import { Button } from "@/components/ui/Button"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { popDialog } from "@/store/dialogs-state"

const FAKE_PROGRESS_DURATION_MS = 20_000
const FAKE_PROGRESS_STEPS = 100
const MAX_PROGRESS = 99

export type HealingAiProgressMode = "ai-erase" | "ai-generate"

const TITLE_KEYS: Record<HealingAiProgressMode, string> = {
	"ai-erase": "healingAiProgressDialog.titleAiErase",
	"ai-generate": "healingAiProgressDialog.titleAiGenerate",
}

const TIP_KEYS: Record<HealingAiProgressMode, string[]> = {
	"ai-erase": [
		"healingAiProgressDialog.tipEraseLargerMask",
		"healingAiProgressDialog.tipEraseUseGenerate",
		"healingAiProgressDialog.tipEraseFeather",
	],
	"ai-generate": [
		"healingAiProgressDialog.tipGenerateBeSpecific",
		"healingAiProgressDialog.tipGenerateExpandMask",
		"healingAiProgressDialog.tipGenerateReference",
		"healingAiProgressDialog.tipGenerateFeather",
	],
}

const tipCounters: Record<HealingAiProgressMode, number> = {
	"ai-erase": 0,
	"ai-generate": 0,
}

export type HealingAiProgressDialogProps = {
	mode: HealingAiProgressMode
	onStart: () => Promise<void>
	onCancel: () => void
}

export const HealingAiProgressDialog = ({
	mode,
	onStart,
	onCancel,
}: HealingAiProgressDialogProps) => {
	const { t } = useTranslation("components")
	const [progress, setProgress] = useState(0)
	const [tip] = useState(() => {
		const tips = TIP_KEYS[mode]
		const idx = tipCounters[mode] % tips.length
		return t(tips[idx] as string)
	})
	const closedByUserRef = useRef(false)
	const startedRef = useRef(false)

	useEffect(() => {
		if (!startedRef.current) {
			startedRef.current = true
			tipCounters[mode]++
			onStart().finally(() => {
				if (!closedByUserRef.current) {
					popDialog(1)
				}
			})
		}

		const intervalMs = FAKE_PROGRESS_DURATION_MS / FAKE_PROGRESS_STEPS
		let step = 0
		const timer = setInterval(() => {
			step += 1
			setProgress(Math.min(MAX_PROGRESS, Math.round((step / FAKE_PROGRESS_STEPS) * 100)))
			if (step >= FAKE_PROGRESS_STEPS) clearInterval(timer)
		}, intervalMs)

		return () => clearInterval(timer)
	}, [onStart])

	const handleRunInBackground = () => {
		closedByUserRef.current = true
		popDialog(1)
	}

	const handleCancel = () => {
		closedByUserRef.current = true
		onCancel()
		popDialog(1)
	}

	return (
		<Dialog trackingViewName="healing_ai_progress_dialog" disableClose>
			<div className="flex w-[462px] flex-col space-y-4">
				<div className="text-md font-bold">{t(TITLE_KEYS[mode])}</div>

				<div className="flex flex-col space-y-2">
					<div
						role="progressbar"
						aria-valuenow={progress}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-label={t(TITLE_KEYS[mode])}
						className="h-2 w-full overflow-hidden rounded-full bg-base-700"
					>
						<div
							className="h-full bg-accent-600 transition-all duration-300"
							style={{ width: `${progress}%` }}
						/>
					</div>
					<div className="text-sm text-base-200">{progress}%</div>
				</div>

				<div className="text-sm text-base-400">{tip}</div>

				<div className="flex flex-row justify-end gap-2">
					<Button
						trackingLabel="healing_ai_run_in_background"
						aria-label={t("healingAiProgressDialog.ariaRunInBackground")}
						onClick={handleRunInBackground}
					>
						{t("healingAiProgressDialog.runInBackground")}
					</Button>
					<Button
						trackingLabel="healing_ai_cancel"
						aria-label={t("healingAiProgressDialog.ariaCancelAIHealing")}
						onClick={handleCancel}
					>
						{t("common:action.cancel")}
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
